import React, { useState, useEffect, useMemo } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Typography, Button, IconButton, Chip, CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import { planDay } from '../utils/dailyPlannerRules';
import { updateTaskSchedule } from '../api/taskApi';
import { showSuccess, showError } from '../utils/toast';
import { useThemeMode } from '../theme/ThemeContext';

const PRIORITY_COLORS = { 1: '#27ae86', 2: '#1976d2', 3: '#ed6c02', 4: '#d32f2f' };
const PRIORITY_LABELS = { 1: 'Standard', 2: 'Important', 3: 'Urgent', 4: 'Critical' };

/**
 * Rule-based daily planner. Gets tasks from the event payload, generates a schedule,
 * and can apply it (updates start_time / end_time for each task).
 *
 * Listens to: 'openDailyPlanner' with detail: { tasks: Task[] }
 * Dispatches: 'bulkActionCompleted' after apply (UserHomepage refreshes)
 */
const DailyPlannerDialog = () => {
    const [open, setOpen] = useState(false);
    const [tasks, setTasks] = useState([]);
    const [applying, setApplying] = useState(false);
    const { themeMode } = useThemeMode();
    const isDark = themeMode === 'dark';

    // Read start/end hour from localStorage if saved
    const startHour = localStorage.getItem('plannerStartHour') || '09:00';
    const endHour = localStorage.getItem('plannerEndHour') || '18:00';

    useEffect(() => {
        const handler = (e) => {
            const incoming = Array.isArray(e.detail?.tasks) ? e.detail.tasks : [];
            setTasks(incoming);
            setOpen(true);
        };
        window.addEventListener('openDailyPlanner', handler);
        return () => window.removeEventListener('openDailyPlanner', handler);
    }, []);

    const { schedule, overflow } = useMemo(
        () => planDay(tasks, startHour, endHour, 0),
        [tasks, startHour, endHour]
    );

    const handleApply = async () => {
        if (schedule.length === 0) return;
        setApplying(true);
        let ok = 0;
        let fail = 0;
        for (const slot of schedule) {
            try {
                const taskId = slot.task.ID || slot.task.id || slot.task.task_id || slot.task.mission_id || slot.task.project_id;
                if (!taskId) { fail++; continue; }
                const today = new Date();
                const yyyy = today.getFullYear();
                const mm = String(today.getMonth() + 1).padStart(2, '0');
                const dd = String(today.getDate()).padStart(2, '0');
                const dateStr = `${yyyy}-${mm}-${dd}`;
                await updateTaskSchedule(taskId, {
                    startDate: dateStr,
                    startTime: slot.startTime,
                    endDate: dateStr,
                    endTime: slot.endTime
                });
                ok++;
            } catch (err) {
                fail++;
            }
        }
        setApplying(false);
        if (ok > 0) {
            showSuccess(`${ok} task${ok > 1 ? 's' : ''} scheduled${fail > 0 ? `, ${fail} failed` : ''}`);
            window.dispatchEvent(new CustomEvent('bulkActionCompleted'));
            setOpen(false);
        } else {
            showError('Failed to apply schedule');
        }
    };

    return (
        <Dialog
            open={open}
            onClose={() => !applying && setOpen(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: '12px', background: isDark ? '#1e1e1e' : '#fff' } }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AutoAwesomeIcon sx={{ color: '#27ae86' }} />
                    <Typography sx={{ fontWeight: 700, fontSize: '16px', color: isDark ? '#e0e0e0' : '#1f2d3d' }}>
                        Plan My Day
                    </Typography>
                </Box>
                <IconButton size="small" onClick={() => setOpen(false)} disabled={applying} sx={{ color: isDark ? '#ccc' : '#666' }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: 1 }}>
                <Typography variant="body2" sx={{ color: isDark ? '#aaa' : '#666', fontSize: '13px', mb: 1.5 }}>
                    Suggested schedule based on priority, deadlines, and effort. Working hours: {startHour}–{endHour}.
                </Typography>

                {schedule.length === 0 ? (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Typography variant="body2" sx={{ color: isDark ? '#888' : '#999', fontSize: '13px' }}>
                            No tasks to schedule
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                        {schedule.map((slot, idx) => {
                            const p = Number(slot.task.priority) || 1;
                            const color = PRIORITY_COLORS[p];
                            return (
                                <Box
                                    key={idx}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.5,
                                        p: 1.25,
                                        borderRadius: '8px',
                                        backgroundColor: isDark ? '#2a2a2a' : '#f8faf9',
                                        borderLeft: `3px solid ${color}`
                                    }}
                                >
                                    <Box sx={{ minWidth: 85, display: 'flex', flexDirection: 'column' }}>
                                        <Typography sx={{ fontSize: '13px', fontWeight: 700, color: isDark ? '#e0e0e0' : '#1f2d3d' }}>
                                            {slot.startTime}
                                        </Typography>
                                        <Typography sx={{ fontSize: '10px', color: isDark ? '#888' : '#999' }}>
                                            → {slot.endTime}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography sx={{ fontSize: '13px', fontWeight: 500, color: isDark ? '#e0e0e0' : '#1f2d3d', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {slot.task.title || 'Untitled'}
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
                                            <AccessTimeIcon sx={{ fontSize: 11, color: isDark ? '#888' : '#999' }} />
                                            <Typography sx={{ fontSize: '11px', color: isDark ? '#888' : '#999' }}>
                                                {slot.durationMin < 60 ? `${slot.durationMin}m` : `${Math.floor(slot.durationMin / 60)}h${slot.durationMin % 60 ? ` ${slot.durationMin % 60}m` : ''}`}
                                            </Typography>
                                            <FlagOutlinedIcon sx={{ fontSize: 11, color, ml: 0.5 }} />
                                            <Typography sx={{ fontSize: '11px', color }}>
                                                {PRIORITY_LABELS[p]}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                )}

                {overflow.length > 0 && (
                    <Box sx={{ mt: 2, p: 1.5, borderRadius: '8px', backgroundColor: isDark ? 'rgba(237,108,2,0.1)' : '#fff8e1' }}>
                        <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#ef6c00', mb: 0.5 }}>
                            {overflow.length} task{overflow.length > 1 ? 's' : ''} didn't fit today
                        </Typography>
                        <Typography sx={{ fontSize: '11px', color: isDark ? '#bbb' : '#777' }}>
                            {overflow.slice(0, 3).map((t) => t.title).join(', ')}
                            {overflow.length > 3 ? `, and ${overflow.length - 3} more` : ''}
                        </Typography>
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={() => setOpen(false)} disabled={applying} sx={{ textTransform: 'none', color: isDark ? '#aaa' : '#666' }}>
                    Cancel
                </Button>
                <Button
                    onClick={handleApply}
                    variant="contained"
                    disabled={applying || schedule.length === 0}
                    startIcon={applying ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : null}
                    sx={{ textTransform: 'none', backgroundColor: '#27ae86', '&:hover': { backgroundColor: '#1f8f6d' } }}
                >
                    {applying ? 'Applying...' : 'Apply schedule'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DailyPlannerDialog;
